import React, { useCallback, useContext, useEffect, useRef } from "react";
import styles from "./KindleProgramBenefitsSheet.module.scss";
import { recordBehavioralMetric, recordMetricOnce } from "src/utils/metricsUtils";
import focusHelper from "src/utils/focusHelper";
import { BDSIcon } from "@amzn/books-design-system-web/build/dist/src/utils/imageUtils";
import { NativeAPI } from "src/utils/deviceUtils";
import useImpressionCallback from "src/utils/useImpressionCallback";
import TranslationsContext from "src/contexts/TranslationsContext";

type PropTypes = {
    data: ProgramBenefitsData;
    dismiss: () => void;
};

const KindleProgramBenefitsSheet: React.FC<PropTypes> = ({ data, dismiss }: PropTypes) => {
    const translations = useContext(TranslationsContext);
    const a11yInitialTarget = useRef<HTMLDivElement>(null);
    const linkoutRef = useRef<HTMLButtonElement>(null);
    const linkUrl = data.programBenefits.link?.url ?? "";
    const linkText = data.programBenefits.link?.data?.displayString;
    const benefits = data.programBenefits.benefits ?? [];
    
    useEffect(() => {
        focusHelper.requestFocus(a11yInitialTarget.current);
    }, []);

    useImpressionCallback(linkoutRef, () => recordMetricOnce({ namespace: "KindleProgramBenefitsSheet", qv_asin: data.asin }, `Linkout.Render`, 1));

    const onLinkTap = useCallback(() => {
        recordBehavioralMetric({ namespace: "KindleProgramBenefitsSheet", qv_asin: data.asin }, "Linkout.Click", 1)
        NativeAPI.openWebPage(linkUrl);
    }, [data.asin, linkUrl])

    const maybeStopPropagation = useCallback((event: React.UIEvent) => {
        const ct = event.currentTarget;
        if (ct.scrollTop || ct.scrollHeight !== ct.clientHeight) {
            event.stopPropagation();
        }
    }, []);

    return (
        <div className={styles.itemContainer}>
            <div className={styles.chevronButtonContainer}>
                <button type="button" tabIndex={0} onClick={dismiss} aria-label={translations.getText("dismiss")}>
                    <div className={styles.chevronIconContainer}>
                        <div className={styles.chevronIcon} onClick={dismiss}/>
                    </div>
                </button>
            </div>
            <header className={styles.header} ref={a11yInitialTarget} tabIndex={-1}>
                {data.programBenefits.title}
            </header>

            <main
                className={styles.main}
                onScroll={maybeStopPropagation}
                onTouchStart={maybeStopPropagation}
                onTouchMove={maybeStopPropagation}
                onTouchEnd={maybeStopPropagation}
                onTouchCancel={maybeStopPropagation}
            >
                <div>
                    {data.programBenefits.description}
                </div>

                {benefits.map((item, index) => {
                    if (!item.icon || !item.information) {
                        return null;
                    }
                    return (<div key={`${item.information}_${index}`} className={styles.benefitsRow}>
                        <div><bds-icon iconName={`${item.icon}.svg` as BDSIcon} iconSize="24px" /></div>
                        <div>{item.information}</div>
                    </div>);
                })}

                {linkUrl && linkText && (<div>
                    <button ref={linkoutRef} className={styles.benefitsLink} onClick={onLinkTap} tabIndex={0}>
                        {data.programBenefits.link?.data?.displayString}
                    </button>
                </div>)}

            </main>
        </div>
    );
};

export default KindleProgramBenefitsSheet;
